

























































import Vue, { PropOptions } from 'vue';
import type { HeadingTag } from '@/components/Heading.vue';
import type { BentoCard } from '@/components/storyblok/BentoCard.vue';
import type { PaddingOptions } from '@/components/SectionContainer.vue';

export type BentoCards = Blok & {
  layout:
    | 'two-cards-layout-1-1'
    | 'two-cards-layout-1-2'
    | 'two-cards-layout-2-1'
    | 'two-cards-layout-1-2-no-alternate'
    | 'two-cards-layout-2-1-no-alternate'
    | 'three-cards-layout';
  cards: BentoCard[];
  mobileChessOrder: boolean;
  showTitle?: boolean;
  id?: string;
  title?: string;
  titleFontSize?: string;
  titleTag: HeadingTag;
  showAdditionalInfo?: boolean;
  additionalInfoText?: string;
  additionalInfoFontSize?: string;
  additionalInfoColor?: string;
  additionalInfoTag: HeadingTag;
  customCss?: string;
  paddingTop?: PaddingOptions;
  paddingBottom?: PaddingOptions;
  backgroundImageDesktop?: BlokFieldAsset;
  backgroundImageMobile?: BlokFieldAsset;
  backgroundImagePosition?: string;
};

export type BentoCardsComponentInstance = Vue & {
  blok: BentoCards;
  calculateWidthsForThree(cards: BentoCard[]): BentoCard[];
  calculateWidths(
    cards: BentoCard[],
    widthProp: 'desktopWidth' | 'tabletWidth',
    firstCardWidth?: number,
    secondCardWidth?: number,
    disableAlternate?: boolean,
    mobileChessOrder?: boolean
  ): BentoCard[];
};

export default Vue.extend({
  props: {
    blok: {
      type: Object,
      required: true,
    } as PropOptions<BentoCards>,
  },
  methods: {
    calculateWidthsForThree(cards: BentoCard[]) {
      const result = [];
      let i = 0;

      while (i < cards.length) {
        const firstCard = cards[i];
        const secondCard = cards[i + 1];
        const thirdCard = cards[i + 2];

        if (i <= cards.length - 3) {
          // Process row of three cards
          if (
            !firstCard.fullWidth &&
            !secondCard.fullWidth &&
            !thirdCard.fullWidth
          ) {
            // 000
            firstCard.desktopWidth = 33.3;
            secondCard.desktopWidth = 33.3;
            thirdCard.desktopWidth = 33.3;
            result.push(firstCard, secondCard, thirdCard);
            i += 3;
          } else if (
            !firstCard.fullWidth &&
            !secondCard.fullWidth &&
            thirdCard.fullWidth
          ) {
            // 001
            firstCard.desktopWidth = 50;
            secondCard.desktopWidth = 50;
            thirdCard.desktopWidth = 100;
            result.push(firstCard, secondCard, thirdCard);
            i += 3;
          } else if (
            !firstCard.fullWidth &&
            secondCard.fullWidth &&
            !thirdCard.fullWidth
          ) {
            // 010
            firstCard.desktopWidth = 100;
            secondCard.desktopWidth = 100;
            result.push(firstCard, secondCard);
            i += 2;
          } else if (secondCard.fullWidth && thirdCard.fullWidth) {
            // 011 or 111
            firstCard.desktopWidth = 100;
            secondCard.desktopWidth = 100;
            thirdCard.desktopWidth = 100;
            result.push(firstCard, secondCard, thirdCard);
            i += 3;
          } else if (
            firstCard.fullWidth &&
            !secondCard.fullWidth &&
            !thirdCard.fullWidth
          ) {
            // 100
            firstCard.desktopWidth = 100;
            result.push(firstCard);
            i += 1;
          } else if (
            firstCard.fullWidth &&
            !secondCard.fullWidth &&
            thirdCard.fullWidth
          ) {
            // 101
            firstCard.desktopWidth = 100;
            secondCard.desktopWidth = 100;
            thirdCard.desktopWidth = 100;
            result.push(firstCard, secondCard, thirdCard);
            i += 3;
          } else if (
            firstCard.fullWidth &&
            secondCard.fullWidth &&
            !thirdCard.fullWidth
          ) {
            // 110
            firstCard.desktopWidth = 100;
            secondCard.desktopWidth = 100;
            result.push(firstCard, secondCard);
            i += 2;
          }
        } else if (i <= cards.length - 2) {
          // Process row of two cards
          if (!firstCard.fullWidth && !secondCard.fullWidth) {
            // 00
            firstCard.desktopWidth = 50;
            secondCard.desktopWidth = 50;
            result.push(firstCard, secondCard);
            i += 2;
          } else {
            // 01 or 10 or 11
            firstCard.desktopWidth = 100;
            secondCard.desktopWidth = 100;
            result.push(firstCard, secondCard);
            i += 2;
          }
        } else if (i <= cards.length - 1) {
          // Process row of one card
          firstCard.desktopWidth = 100;
          result.push(firstCard);
          i += 1;
        }
      }

      return result;
    },

    calculateWidths(
      cards: BentoCard[],
      widthProp: 'desktopWidth' | 'tabletWidth',
      firstCardWidth: number = 50,
      secondCardWidth: number = 50,
      disableAlternate?: boolean,
      mobileChessOrder?: boolean
    ) {
      const result = [];
      let i = 0;
      let alternate = false;

      while (i < cards.length) {
        // If there's only one card left, make it full width
        if (i === cards.length - 1) {
          cards[i][widthProp] = 100;
          cards[i].desktopOrder = i + 1;
          cards[i].mobileOrder = i + 1;

          result.push(cards[i]);
          break;
        }

        const firstCard = cards[i];
        const secondCard = cards[i + 1];

        // add desktop order
        firstCard.desktopOrder = i + 1;
        secondCard.desktopOrder = i + 2;

        // add mobile order
        if (mobileChessOrder && (i / 2) % 2 === 1) {
          // apply only on 2nd, 4th, 6th, etc row
          firstCard.mobileOrder = i + 2; // swapped order
          secondCard.mobileOrder = i + 1; // swapped order
        } else {
          firstCard.mobileOrder = i + 1;
          secondCard.mobileOrder = i + 2;
        }

        if (!firstCard.fullWidth && !secondCard.fullWidth) {
          // Both cards are regular
          if (alternate) {
            firstCard[widthProp] = secondCardWidth;
            secondCard[widthProp] = firstCardWidth;
          } else {
            firstCard[widthProp] = firstCardWidth;
            secondCard[widthProp] = secondCardWidth;
          }

          if (!disableAlternate) {
            alternate = !alternate; // Toggle the alternating state
          }

          result.push(firstCard, secondCard);
          i += 2;
        } else if (firstCard.fullWidth && !secondCard.fullWidth) {
          // First card is full width, second is regular
          firstCard[widthProp] = 100;
          result.push(firstCard);
          i += 1;
        } else {
          // Second card is full width
          firstCard[widthProp] = 100;
          secondCard[widthProp] = 100;
          result.push(firstCard, secondCard);
          i += 2;
        }
      }

      return result;
    },
  },
  computed: {
    processedCards(this: BentoCardsComponentInstance) {
      let desktopCards: BentoCard[] = [];

      const isNoAlternate =
        this.blok.layout === 'two-cards-layout-1-2-no-alternate' ||
        this.blok.layout === 'two-cards-layout-2-1-no-alternate';

      if (this.blok.layout === 'two-cards-layout-1-1') {
        desktopCards = this.calculateWidths(
          this.blok.cards,
          'desktopWidth',
          50,
          50,
          false,
          this.blok.mobileChessOrder
        );
      } else if (
        this.blok.layout === 'two-cards-layout-1-2' ||
        this.blok.layout === 'two-cards-layout-1-2-no-alternate'
      ) {
        desktopCards = this.calculateWidths(
          this.blok.cards,
          'desktopWidth',
          35,
          65,
          isNoAlternate,
          this.blok.mobileChessOrder
        );
      } else if (
        this.blok.layout === 'two-cards-layout-2-1' ||
        this.blok.layout === 'two-cards-layout-2-1-no-alternate'
      ) {
        desktopCards = this.calculateWidths(
          this.blok.cards,
          'desktopWidth',
          65,
          35,
          isNoAlternate,
          this.blok.mobileChessOrder
        );
      } else if (this.blok.layout === 'three-cards-layout') {
        desktopCards = this.calculateWidthsForThree(this.blok.cards);
      }

      return this.calculateWidths(
        desktopCards,
        'tabletWidth',
        50,
        50,
        isNoAlternate,
        this.blok.mobileChessOrder
      );
    },
    additionalInfoStyle(): {
      color?: string;
    } {
      return {
        color: this.blok.additionalInfoColor
          ? `var(--${this.blok.additionalInfoColor})`
          : undefined,
      };
    },
    customCssStyle(): { [key: string]: string } {
      // convert custom css string to object
      if (this.blok.customCss) {
        const css = this.blok.customCss
          .split(';')
          .reduce((acc: { [key: string]: string }, css: string) => {
            const [key, value] = css.split(':');
            if (key && value) {
              acc[key.trim()] = value.trim();
            }
            return acc;
          }, {});
        return css;
      }
      return {}; // Return an empty object if this.blok.customCss is not defined
    },
  },
});
